import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { Archive, BuildingFill, ChevronDown, ChevronRight, Dot, FolderFill } from 'react-bootstrap-icons'
import axiosService from '../../../utils/AxiosService'
import { toastError, toastSuccess } from '../../FormComponents/Toasts'
import { BrowseContext } from '../../../contexts/BrowseContext'
import { useSidebarCollapse } from '../../../contexts/SidebarCollapseContext'
import ArchiveModal, { pluralize } from './ArchiveModal'

const RESTORE_COPY = {
  clients: {
    title: 'Restore client',
    description: 'The client and all of its folders and campaigns will be restored and become editable again. Its client share link will work again.',
  },
  folders: {
    title: 'Restore folder',
    description: 'The folder and all of its campaigns will be restored and become editable again. Its client is restored too if it was archived.',
  },
  campaigns: {
    title: 'Restore campaign',
    description: 'The campaign will become editable again. Its folder and client are restored too if they were archived.',
  },
}

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString() : ''

const archivedLabel = (node) =>
  `Archived ${formatDate(node.archived_at)}${node.archived_by_name ? ` by ${node.archived_by_name}` : ''}`

function RestoreButton({ onClick }) {
  return (
    <button
      type='button'
      className='px-2 py-1 rounded-md text-sm text-rb-green bg-rb-soft-green dark:bg-rb-green/20 flex-shrink-0'
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      Restore
    </button>
  )
}

function StatusLabel({ node }) {
  if (node.archived_at) return <span className='rb-text-muted text-sm'>{archivedLabel(node)}</span>

  return (
    <span className='px-2 py-0.5 rounded-full text-xs bg-rb-soft-green dark:bg-rb-green/20 text-rb-green'>Active</span>
  )
}

function Chevron({ isOpen }) {
  const Icon = isOpen ? ChevronDown : ChevronRight
  return <Icon className='flex-shrink-0 rb-text-muted' />
}

function summary(parts) {
  return parts.filter(([count]) => count > 0).map(([count, noun]) => pluralize(count, noun)).join(' · ')
}

function CampaignCards({ campaigns, onRestore }) {
  const navigate = useNavigate()

  return (
    <div className='grid grid-cols-3 gap-4'>
      {campaigns.map(campaign => (
        <div
          key={campaign.id}
          className='border-1 border-rb-lighter-grey dark:border-rb-dark-mode-lighter rounded-lg bg-white dark:bg-rb-dark-mode flex flex-col justify-between gap-3 p-3 hover:cursor-pointer'
          onClick={() => navigate(`/project_manager/campaigns/${campaign.id}/overview`)}
        >
          <div className='flex items-center'>
            <span className='h-10 w-10 rounded-lg text-lg flex items-center justify-center flex-shrink-0 opacity-60' style={{ backgroundColor: campaign.theme_color }}>
              <span className='text-white'>{campaign.name.slice(0, 2).toUpperCase()}</span>
            </span>
            <div className='pl-3 min-w-0'>
              <h4 className='text-md font-bold dark:text-white truncate'>{campaign.name}</h4>
              {campaign.project_number && <p className='rb-text-muted text-sm truncate'>{campaign.project_number}</p>}
            </div>
          </div>
          <div className='flex items-center justify-between gap-2'>
            <span className='rb-text-muted text-sm'>{archivedLabel(campaign)}</span>
            <RestoreButton onClick={() => onRestore('campaigns', campaign)} />
          </div>
        </div>
      ))}
    </div>
  )
}

export default observer(function ArchiveIndex() {
  const { resultsFilter, reloadBrowse } = useContext(BrowseContext)
  const { isCollapsed } = useSidebarCollapse()
  const [tree, setTree] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [openClients, setOpenClients] = useState(new Set())
  const [openFolders, setOpenFolders] = useState(new Set())
  const [restoreTarget, setRestoreTarget] = useState(null)

  const toggle = (setter, id) => setter(current => {
    const next = new Set(current)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const loadTree = () => {
    setIsLoading(true)
    axiosService.get('/api/project_manager/archive', { params: { filter: resultsFilter } })
      .then(result => setTree(result))
      .catch(({ data: { error } }) => toastError(error))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => loadTree(), [resultsFilter])

  const restore = (resource, node) => setRestoreTarget({ resource, node })

  const confirmRestore = () => {
    const { resource, node } = restoreTarget
    return axiosService.patch(`/api/project_manager/${resource}/${node.id}/unarchive`)
      .then(() => {
        toastSuccess(`${node.name} has been restored`)
        loadTree()
        reloadBrowse()
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  const itemCount = tree.reduce((count, client) =>
    count
    + (client.archived_at ? 1 : 0)
    + client.campaigns.length
    + client.folders.reduce((folderCount, folder) => folderCount + (folder.archived_at ? 1 : 0) + folder.campaigns.length, 0)
  , 0)

  return (
    <div className={`fixed pt-16 ${isCollapsed ? 'pl-32' : 'pl-56'} w-full h-full bg-rb-grey dark:bg-rb-dark-mode-background -z-10 overflow-y-auto transition-all duration-300`}>
      <ArchiveModal
        isOpen={!!restoreTarget}
        onClose={() => setRestoreTarget(null)}
        title={restoreTarget && RESTORE_COPY[restoreTarget.resource].title}
        name={restoreTarget?.node.name}
        description={restoreTarget && RESTORE_COPY[restoreTarget.resource].description}
        onConfirm={confirmRestore}
        verb='restore'
        confirmLabel='Restore'
        pendingLabel='Restoring...'
      />
      <div className='p-8'>
        <div className='flex items-center mb-1'>
          <h2 className='font-bold text-lg dark:text-white'>Archive</h2>
          <Dot className='h-7 w-7 rb-text-muted' />
          <span className='font-bold text-lg rb-text-muted'>{itemCount}</span>
        </div>
        <p className='rb-text-muted text-sm mb-6'>
          Archived clients, folders and campaigns are read only. Restoring an item restores everything inside it, and brings back its client and folder if those were archived.
        </p>

        {!isLoading && tree.length === 0 && (
          <div className='flex flex-col items-center justify-center gap-2 py-16 rb-text-muted'>
            <Archive className='text-3xl' />
            <span>Nothing archived</span>
          </div>
        )}

        {tree.map(client => {
          const isClientOpen = openClients.has(client.id)
          const campaignTotal = client.campaigns.length + client.folders.reduce((total, folder) => total + folder.campaigns.length, 0)

          return (
            <section key={client.id} className='mb-3 rounded-xl border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter bg-white/60 dark:bg-rb-dark-mode/60'>
              <div
                className='flex items-center justify-between gap-3 p-4 cursor-pointer select-none'
                onClick={() => toggle(setOpenClients, client.id)}
              >
                <div className='flex items-center gap-3 min-w-0'>
                  <Chevron isOpen={isClientOpen} />
                  <BuildingFill className='flex-shrink-0' style={{ color: client.theme_color || undefined }} />
                  <h3 className='font-bold text-lg dark:text-white truncate'>{client.name}</h3>
                  <StatusLabel node={client} />
                  <span className='rb-text-muted text-sm whitespace-nowrap'>{summary([[client.folders.length, 'folder'], [campaignTotal, 'campaign']])}</span>
                </div>
                {client.archived_at && <RestoreButton onClick={() => restore('clients', client)} />}
              </div>

              {isClientOpen && (
                <div className='px-4 pb-4'>
                  {client.folders.map(folder => {
                    const isFolderOpen = openFolders.has(folder.id)

                    return (
                      <div key={folder.id} className='mb-3 ml-4 pl-4 border-l-2 border-rb-widget-border dark:border-rb-dark-mode-lighter'>
                        <div
                          className='flex items-center justify-between gap-3 py-1 cursor-pointer select-none'
                          onClick={() => toggle(setOpenFolders, folder.id)}
                        >
                          <div className='flex items-center gap-2 min-w-0'>
                            <Chevron isOpen={isFolderOpen} />
                            <FolderFill className='rb-text-muted flex-shrink-0' />
                            <h4 className='font-bold dark:text-white truncate'>{folder.name}</h4>
                            <StatusLabel node={folder} />
                            <span className='rb-text-muted text-sm whitespace-nowrap'>{summary([[folder.campaigns.length, 'campaign']]) || 'Empty'}</span>
                          </div>
                          {folder.archived_at && <RestoreButton onClick={() => restore('folders', folder)} />}
                        </div>
                        {isFolderOpen && (
                          <div className='mt-3'>
                            {folder.campaigns.length > 0
                              ? <CampaignCards campaigns={folder.campaigns} onRestore={restore} />
                              : <p className='rb-text-muted text-sm'>No campaigns in this folder</p>}
                          </div>
                        )}
                      </div>
                    )
                  })}

                  {client.campaigns.length > 0 && (
                    <div className='ml-4 pl-4 border-l-2 border-rb-widget-border dark:border-rb-dark-mode-lighter'>
                      {client.folders.length > 0 && <p className='rb-text-muted text-sm mb-3'>Not in a folder</p>}
                      <CampaignCards campaigns={client.campaigns} onRestore={restore} />
                    </div>
                  )}

                  {client.folders.length === 0 && client.campaigns.length === 0 && (
                    <p className='ml-4 rb-text-muted text-sm'>No folders or campaigns</p>
                  )}
                </div>
              )}
            </section>
          )
        })}
      </div>
    </div>
  )
})
